import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PlayerGameLogPosition, PlayerGameLogRow, PlayerGameLogScoring, PlayerSeasonGameLogs } from "../../shared/data/playerGameLogs";
import { getComparableGameRows } from "../../shared/utils/playerStatBaselines";
import { PLAYER_PROFILE_STAT_COLOR_CODING_ENABLED, buildHeatmapScales, getHeatmapCellStyle, getHeatmapTier } from "../../shared/utils/playerStatHeatmap";
import {
  PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX,
  PlayerStatsGridZoomControl,
  getPlayerStatsGridLayout,
} from "./playerStatsGridLayout";

type Props = {
  seasons: PlayerSeasonGameLogs[];
  position?: PlayerGameLogPosition | null;
  selectedScoring?: PlayerGameLogScoring;
  onSelectedScoringChange?: (scoring: PlayerGameLogScoring) => void;
  selectedSeason?: number | null;
  onSelectedSeasonChange?: (season: number) => void;
  statsView: "logs" | "career";
  onStatsViewChange: (view: "logs" | "career") => void;
  showPerformanceChart?: boolean;
  getPerformanceBarColor?: (value: number) => string;
  performanceChartLegendItems?: Array<{ label: string; color: string }>;
  gridZoomIndex?: number;
  onGridZoomIndexChange?: React.Dispatch<React.SetStateAction<number>>;
};

type StatTabKey = "passing" | "rushing" | "receiving" | "misc";

type Column = {
  label: string;
  align?: "left" | "center";
  highlight?: boolean;
  lowerIsBetter?: boolean;
  getValue: (row: PlayerGameLogRow) => number | string | undefined;
  digits?: number;
  fixedDigits?: boolean;
};

type ColumnGroup = {
  label: string;
  columns: Column[];
};

function formatMetric(value: number | string | undefined, digits = 1, fixedDigits = false): string {
  if (value == null) return "-";
  if (typeof value === "number") {
    if (value === 0) return "-";
    if (fixedDigits) return value.toFixed(digits);
    return Number.isInteger(value) ? String(value) : value.toFixed(digits);
  }
  return String(value);
}

function safeRate(numerator: number | undefined, denominator: number | undefined): number | undefined {
  if (numerator == null || denominator == null || denominator === 0) return undefined;
  return numerator / denominator;
}

function getFantasyPointsForScoring(row: PlayerGameLogRow, scoring: PlayerGameLogScoring): number | undefined {
  if (scoring === "standard") return row.fantasyPointsStandard ?? row.fantasyPoints;
  if (scoring === "halfPpr") return row.fantasyPointsHalfPpr ?? row.fantasyPoints;
  return row.fantasyPointsPpr ?? row.fantasyPoints;
}

function getColumnGroups(position: PlayerGameLogPosition | null | undefined, scoring: PlayerGameLogScoring): ColumnGroup[] {
  const base: ColumnGroup[] = [
    {
      label: "",
      columns: [
        { label: "WK", getValue: (row) => row.week },
        { label: "OPP", align: "left", getValue: (row) => row.note ?? row.opponent },
      ],
    },
    {
      label: "Fantasy",
      columns: [
        { label: "FPTS", highlight: true, getValue: (row) => getFantasyPointsForScoring(row, scoring), digits: 1, fixedDigits: true },
        { label: "SNP%", getValue: (row) => row.snapPct, digits: 0 },
      ],
    },
  ];

  if (position === "QB") {
    return [
      ...base,
      {
        label: "Pass",
        columns: [
          { label: "ATT", getValue: (row) => row.passingAttempts },
          { label: "CMP", getValue: (row) => row.passingCompletions },
          { label: "YD", getValue: (row) => row.passingYards },
          { label: "TD", getValue: (row) => row.passingTds },
          { label: "INT", lowerIsBetter: true, getValue: (row) => row.passingInterceptions },
        ],
      },
      {
        label: "Rushing",
        columns: [
          { label: "ATT", getValue: (row) => row.rushAttempts },
          { label: "YD", getValue: (row) => row.rushYards },
          { label: "YPC", getValue: (row) => safeRate(row.rushYards, row.rushAttempts), digits: 2 },
          { label: "TD", getValue: (row) => row.rushTds },
        ],
      },
      {
        label: "Sacked",
        columns: [
          { label: "SK", lowerIsBetter: true, getValue: (row) => row.sacks },
          { label: "YDS", lowerIsBetter: true, getValue: (row) => row.sackYardsLost },
        ],
      },
      {
        label: "Fumble",
        columns: [
          { label: "FUM", lowerIsBetter: true, getValue: (row) => row.fumbles },
          { label: "LOST", lowerIsBetter: true, getValue: (row) => row.fumblesLost },
        ],
      },
    ];
  }

  if (position === "RB") {
    return [
      ...base,
      {
        label: "Rushing",
        columns: [
          { label: "ATT", getValue: (row) => row.rushAttempts },
          { label: "YD", getValue: (row) => row.rushYards },
          { label: "YPC", getValue: (row) => safeRate(row.rushYards, row.rushAttempts), digits: 2 },
          { label: "TD", getValue: (row) => row.rushTds },
        ],
      },
      {
        label: "Receiving",
        columns: [
          { label: "TAR", getValue: (row) => row.targets },
          { label: "REC", getValue: (row) => row.receptions },
          { label: "YD", getValue: (row) => row.receivingYards },
          { label: "YPT", getValue: (row) => safeRate(row.receivingYards, row.targets), digits: 2 },
          { label: "YPC", getValue: (row) => safeRate(row.receivingYards, row.receptions), digits: 2 },
          { label: "TD", getValue: (row) => row.receivingTds },
        ],
      },
      {
        label: "Fumble",
        columns: [
          { label: "FUM", lowerIsBetter: true, getValue: (row) => row.fumbles },
          { label: "LOST", lowerIsBetter: true, getValue: (row) => row.fumblesLost },
        ],
      },
      {
        label: "Returning",
        columns: [
          { label: "KR", getValue: (row) => row.kickoffReturns },
          { label: "KYD", getValue: (row) => row.kickoffReturnYards },
          { label: "PR", getValue: (row) => row.puntReturns },
          { label: "SP TD", getValue: (row) => row.specialTeamsTds },
        ],
      },
    ];
  }

  return [
    ...base,
    {
      label: "Receiving",
      columns: [
        { label: "TAR", getValue: (row) => row.targets },
        { label: "REC", getValue: (row) => row.receptions },
        { label: "YD", getValue: (row) => row.receivingYards },
        { label: "YPT", getValue: (row) => safeRate(row.receivingYards, row.targets), digits: 2 },
        { label: "YPC", getValue: (row) => safeRate(row.receivingYards, row.receptions), digits: 2 },
        { label: "TD", getValue: (row) => row.receivingTds },
      ],
    },
    {
      label: "Rushing",
      columns: [
        { label: "ATT", getValue: (row) => row.rushAttempts },
        { label: "YD", getValue: (row) => row.rushYards },
        { label: "YPC", getValue: (row) => safeRate(row.rushYards, row.rushAttempts), digits: 2 },
        { label: "TD", getValue: (row) => row.rushTds },
      ],
    },
    {
      label: "Fumble",
      columns: [
        { label: "FUM", lowerIsBetter: true, getValue: (row) => row.fumbles },
        { label: "LOST", lowerIsBetter: true, getValue: (row) => row.fumblesLost },
      ],
    },
    {
      label: "Returning",
      columns: [
        { label: "KR", getValue: (row) => row.kickoffReturns },
        { label: "KYD", getValue: (row) => row.kickoffReturnYards },
        { label: "PR", getValue: (row) => row.puntReturns },
        { label: "SP TD", getValue: (row) => row.specialTeamsTds },
      ],
    },
  ];
}

function getStatTabs(position: PlayerGameLogPosition | null | undefined): Array<{ key: StatTabKey; label: string }> {
  if (position === "QB") {
    return [
      { key: "passing", label: "Passing" },
      { key: "rushing", label: "Rushing" },
      { key: "misc", label: "Misc" },
    ];
  }

  if (position === "RB") {
    return [
      { key: "rushing", label: "Rushing" },
      { key: "receiving", label: "Receiving" },
      { key: "misc", label: "Misc" },
    ];
  }

  return [
    { key: "receiving", label: "Receiving" },
    { key: "rushing", label: "Rushing" },
    { key: "misc", label: "Misc" },
  ];
}

function getVisibleColumnGroups(
  allGroups: ColumnGroup[],
  selectedTab: StatTabKey,
  position: PlayerGameLogPosition | null | undefined
): ColumnGroup[] {
  const baseGroups = allGroups.slice(0, 2);
  const extraGroups = allGroups.slice(2);

  let targetLabel = "";
  if (selectedTab === "passing") targetLabel = "Pass";
  else if (selectedTab === "rushing") targetLabel = "Rushing";
  else if (selectedTab === "receiving") targetLabel = "Receiving";
  else if (selectedTab === "misc") {
    if (position === "QB") {
      return [
        ...baseGroups,
        ...extraGroups.filter((group) => group.label === "Sacked" || group.label === "Fumble"),
      ];
    }
    return [
      ...baseGroups,
      ...extraGroups.filter((group) => group.label === "Fumble" || group.label === "Returning"),
    ];
  }

  const targetGroup = extraGroups.find((group) => group.label === targetLabel);
  return targetGroup ? [...baseGroups, targetGroup] : baseGroups;
}

function getDefaultPerformanceBarColor(value: number): string {
  if (value <= 0) return "#f87171";
  if (value < 18) return "#f9c74f";
  return "#57c56f";
}

export default function PlayerGameLogTable({
  seasons,
  position,
  selectedScoring: selectedScoringProp,
  onSelectedScoringChange,
  selectedSeason: selectedSeasonProp,
  onSelectedSeasonChange,
  statsView,
  onStatsViewChange,
  showPerformanceChart = false,
  getPerformanceBarColor = getDefaultPerformanceBarColor,
  performanceChartLegendItems = [],
  gridZoomIndex: gridZoomIndexProp,
  onGridZoomIndexChange,
}: Props) {
  const rootRef = useRef<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window === "undefined" ? 844 : window.innerHeight
  );
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const sortedSeasons = useMemo(
    () => [...seasons].sort((a, b) => b.season - a.season),
    [seasons]
  );
  const [selectedSeasonInternal, setSelectedSeasonInternal] = useState<number | null>(sortedSeasons[0]?.season ?? null);
  const [selectedScoringInternal, setSelectedScoringInternal] = useState<PlayerGameLogScoring>(() => {
    if (typeof window === "undefined") return "ppr";
    const saved = window.localStorage.getItem("fantasy-board:profile-scoring");
    return saved === "standard" || saved === "halfPpr" || saved === "ppr" ? saved : "ppr";
  });
  const statTabs = useMemo(() => getStatTabs(position), [position]);
  const [selectedTab, setSelectedTab] = useState<StatTabKey>(statTabs[0]?.key ?? "misc");
  const [performanceChartHidden, setPerformanceChartHidden] = useState(false);
  const [gridZoomIndexInternal, setGridZoomIndexInternal] = useState(PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX);
  const responsiveWidth = containerWidth || viewportWidth;
  const mobileMode = responsiveWidth <= 1100;
  const compactTableMode = responsiveWidth <= 760;
  const showScoringTabs = position !== "QB";
  const selectedScoring = selectedScoringProp ?? selectedScoringInternal;
  const selectedSeason = selectedSeasonProp ?? selectedSeasonInternal;
  const gridZoomIndex = gridZoomIndexProp ?? gridZoomIndexInternal;
  const setGridZoomIndex = onGridZoomIndexChange ?? setGridZoomIndexInternal;

  const setSelectedScoring = (scoring: PlayerGameLogScoring) => {
    if (selectedScoringProp == null) {
      setSelectedScoringInternal(scoring);
    }
    onSelectedScoringChange?.(scoring);
  };

  const setSelectedSeason = useCallback(
    (season: number | null) => {
      if (selectedSeasonProp == null) {
        setSelectedSeasonInternal(season);
      }
      if (season != null) {
        onSelectedSeasonChange?.(season);
      }
    },
    [onSelectedSeasonChange, selectedSeasonProp]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const element = rootRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, [selectedSeason, selectedScoring, statsView]);

  useEffect(() => {
    setSelectedTab(statTabs[0]?.key ?? "misc");
  }, [statTabs]);

  const columnGroups = useMemo(
    () => getVisibleColumnGroups(getColumnGroups(position, selectedScoring), selectedTab, position),
    [position, selectedScoring, selectedTab]
  );
  const flatColumns = useMemo(() => columnGroups.flatMap((group) => group.columns), [columnGroups]);

  React.useEffect(() => {
    const hasSelected = sortedSeasons.some((season) => season.season === selectedSeason);
    if (!hasSelected) {
      setSelectedSeason(sortedSeasons[0]?.season ?? null);
    }
  }, [selectedSeason, setSelectedSeason, sortedSeasons]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = 0;
  }, [selectedSeason, selectedScoring]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("fantasy-board:profile-scoring", selectedScoring);
  }, [selectedScoring]);

  const activeSeason = sortedSeasons.find((season) => season.season === selectedSeason) ?? sortedSeasons[0] ?? null;
  const displayRows = useMemo(() => {
    if (!activeSeason) return [];

    const byWeek = new Map(activeSeason.rows.map((row) => [row.week, row]));
    const maxWeek = Math.max(18, ...activeSeason.rows.map((row) => row.week));
    const rows: PlayerGameLogRow[] = [];

    for (let week = 1; week <= maxWeek; week++) {
      rows.push(
        byWeek.get(week) ?? {
          week,
          opponent: "-",
        }
      );
    }

    return rows;
  }, [activeSeason]);
  const performanceChartRows = useMemo(() => {
    return displayRows.map((row) => {
      const points = getFantasyPointsForScoring(row, selectedScoring) ?? 0;
      return {
        week: row.week,
        points,
        label: points.toFixed(1),
        color: getPerformanceBarColor(points),
      };
    });
  }, [displayRows, getPerformanceBarColor, selectedScoring]);
  const heatmapScales = useMemo(
    () =>
      buildHeatmapScales(
        activeSeason ? getComparableGameRows(position, activeSeason.season) : [],
        flatColumns.slice(2).map((column, index) => ({
          key: String(index + 2),
          lowerIsBetter: column.lowerIsBetter,
          getNumericValue: (row: PlayerGameLogRow) => {
            const value = column.getValue(row);
            return typeof value === "number" ? value : undefined;
          },
        }))
      ),
    [activeSeason, flatColumns, position]
  );

  if (!activeSeason) return null;

  const activeSeasonIndex = sortedSeasons.findIndex((season) => season.season === activeSeason.season);
  const olderSeason = activeSeasonIndex >= 0 ? sortedSeasons[activeSeasonIndex + 1] ?? null : null;
  const newerSeason = activeSeasonIndex > 0 ? sortedSeasons[activeSeasonIndex - 1] ?? null : null;
  const chartMax = Math.max(50, ...performanceChartRows.map((row) => row.points));
  const chartTicks = [0, 10, 20, 30, 40, 50].filter((tick) => tick <= chartMax);
  const {
    firstColumnWidth,
    secondColumnWidth,
    headerFontSize,
    cellFontSize,
    cellPadding,
    statPillPadding,
    statPillRadius,
  } = getPlayerStatsGridLayout({ mobileMode, compactTableMode, gridZoomIndex });
  const logFirstColumnWidth = mobileMode ? Math.max(34, Math.round(firstColumnWidth * 0.72)) : firstColumnWidth;
  const logSecondColumnWidth = mobileMode ? Math.max(38, Math.round(secondColumnWidth * 0.86)) : secondColumnWidth;
  const profileControlsLandscape = mobileMode && viewportWidth > viewportHeight;
  const profileControlFontSize = profileControlsLandscape ? 10 : mobileMode ? 11 : 12;
  const profileSmallControlFontSize = profileControlsLandscape ? 9 : mobileMode ? 10 : 12;
  const profileControlPaddingY = profileControlsLandscape ? 3 : mobileMode ? 5 : 6;
  const profileControlPaddingX = profileControlsLandscape ? 7 : mobileMode ? 10 : 12;
  const profileSmallControlPaddingX = profileControlsLandscape ? 6 : mobileMode ? 8 : 10;
  const profileControlMinHeight = profileControlsLandscape ? 24 : undefined;
  const profileControlGap = profileControlsLandscape ? 4 : mobileMode ? 6 : 10;
  const gridCellStyle: React.CSSProperties = {
    padding: cellPadding,
    textAlign: "center",
    fontSize: cellFontSize,
    fontWeight: 800,
    color: "#fff",
    whiteSpace: "nowrap",
  };
  const gridNumericCellStyle: React.CSSProperties = {
    ...gridCellStyle,
    overflow: "visible",
  };
  const gridHighlightCellStyle: React.CSSProperties = {
    ...gridNumericCellStyle,
    fontWeight: 900,
  };

  const getColumnWidth = (column: Column, index: number) => {
    if (mobileMode) {
      if (index === 0) return `${logFirstColumnWidth}px`;
      if (index === 1) return `${logSecondColumnWidth}px`;
      return undefined;
    }

    if (index === 0) return `${firstColumnWidth}px`;
    if (index === 1) return `${secondColumnWidth}px`;
    if (column.highlight) return "76px";
    if (column.label === "SNP%") return "74px";
    return undefined;
  };

  const seasonControl = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: 2,
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.08)",
        flex: "0 0 auto",
      }}
    >
      <button
        type="button"
        onClick={() => newerSeason && setSelectedSeason(newerSeason.season)}
        disabled={!newerSeason}
        style={{
          appearance: "none",
          border: "none",
          cursor: newerSeason ? "pointer" : "default",
          borderRadius: 999,
          padding: `${profileControlPaddingY}px ${profileSmallControlPaddingX}px`,
          background: "transparent",
          color: newerSeason ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.28)",
          fontWeight: 900,
          fontSize: profileControlFontSize,
          minHeight: profileControlMinHeight,
          lineHeight: 1,
        }}
      >
        {"<"}
      </button>
      <div
        style={{
          borderRadius: 999,
          padding: `${profileControlPaddingY}px ${profileControlPaddingX + 2}px`,
          background: "rgba(255,255,255,0.92)",
          color: "#0f172a",
          fontWeight: 900,
          fontSize: profileControlFontSize,
          lineHeight: 1,
          whiteSpace: "nowrap",
          minWidth: profileControlsLandscape ? 54 : mobileMode ? 62 : 72,
          minHeight: profileControlMinHeight,
          textAlign: "center",
        }}
      >
        {activeSeason.season}
      </div>
      <button
        type="button"
        onClick={() => olderSeason && setSelectedSeason(olderSeason.season)}
        disabled={!olderSeason}
        style={{
          appearance: "none",
          border: "none",
          cursor: olderSeason ? "pointer" : "default",
          borderRadius: 999,
          padding: `${profileControlPaddingY}px ${profileSmallControlPaddingX}px`,
          background: "transparent",
          color: olderSeason ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.28)",
          fontWeight: 900,
          fontSize: profileControlFontSize,
          minHeight: profileControlMinHeight,
          lineHeight: 1,
        }}
      >
        {">"}
      </button>
    </div>
  );

  const controls = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: profileControlGap,
        flexWrap: "wrap",
        justifyContent: mobileMode ? "flex-start" : "flex-end",
        minWidth: 0,
        flex: mobileMode ? "1 1 auto" : undefined,
        paddingBottom: profileControlsLandscape ? 2 : mobileMode ? 4 : undefined,
        overflow: "visible",
      }}
    >
      {mobileMode ? (
        <div style={{ flex: profileControlsLandscape ? "0 0 auto" : "0 0 100%", display: "flex", justifyContent: "flex-start" }}>
          {seasonControl}
        </div>
      ) : null}
      <div
        style={{
          display: "inline-flex",
          gap: 2,
          padding: 2,
          borderRadius: 999,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          flex: mobileMode ? "0 0 auto" : undefined,
          minWidth: 0,
        }}
      >
        {([
          { key: "logs", label: "Logs" },
          { key: "career", label: "Career" },
        ] as const).map((option) => {
          const isActive = option.key === statsView;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onStatsViewChange(option.key)}
              style={{
                appearance: "none",
                border: "none",
                cursor: "pointer",
                borderRadius: 999,
                padding: `${profileControlPaddingY}px ${profileControlPaddingX}px`,
                background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                fontWeight: 900,
                fontSize: profileControlFontSize,
                minHeight: profileControlMinHeight,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <div
        style={{
          display: "inline-flex",
          gap: 2,
          padding: 2,
          borderRadius: 999,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          flex: mobileMode ? "0 0 auto" : undefined,
          minWidth: mobileMode ? "max-content" : 0,
        }}
      >
        {statTabs.map((tab) => {
          const isActive = tab.key === selectedTab;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSelectedTab(tab.key)}
              style={{
                appearance: "none",
                border: "none",
                cursor: "pointer",
                borderRadius: 999,
                padding: `${profileControlPaddingY}px ${profileSmallControlPaddingX}px`,
                background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                fontWeight: 900,
                fontSize: profileSmallControlFontSize,
                minHeight: profileControlMinHeight,
                lineHeight: 1,
                flex: mobileMode ? "0 0 auto" : undefined,
                minWidth: mobileMode ? "max-content" : 0,
                whiteSpace: "nowrap",
              }}
            >
              {mobileMode
                ? tab.label === "Receiving"
                  ? "Rec"
                  : tab.label === "Rushing"
                    ? "Rush"
                    : tab.label
                : tab.label}
            </button>
          );
        })}
      </div>
      {showScoringTabs ? (
        <div
            style={{
              display: "inline-flex",
              gap: 2,
              padding: 2,
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
            flex: mobileMode ? "0 0 auto" : undefined,
            minWidth: mobileMode ? "max-content" : 0,
          }}
        >
          {[
            { key: "standard", label: "STD" },
            { key: "halfPpr", label: "HALF-PPR" },
            { key: "ppr", label: "PPR" },
          ].map((option) => {
            const isActive = option.key === selectedScoring;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setSelectedScoring(option.key as PlayerGameLogScoring)}
                style={{
                  appearance: "none",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: 999,
                  padding: `${profileControlPaddingY}px ${profileSmallControlPaddingX}px`,
                  background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                  color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                  fontWeight: 900,
                  fontSize: profileSmallControlFontSize,
                  minHeight: profileControlMinHeight,
                  lineHeight: 1,
                  flex: mobileMode ? "0 0 auto" : undefined,
                  minWidth: mobileMode ? "max-content" : 0,
                  whiteSpace: "nowrap",
                }}
              >
                {mobileMode && option.key === "halfPpr" ? "HALF" : option.label}
              </button>
            );
          })}
        </div>
      ) : null}
      {!mobileMode ? seasonControl : null}
      <PlayerStatsGridZoomControl
        gridZoomIndex={gridZoomIndex}
        setGridZoomIndex={setGridZoomIndex}
        label="logs"
        mobileMode={mobileMode}
        compactMode={profileControlsLandscape}
      />
    </div>
  );

  return (
    <section
      ref={rootRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: mobileMode ? 8 : 10,
        minWidth: 0,
        minHeight: 0,
        height: mobileMode ? "100%" : undefined,
        flex: mobileMode ? "1 1 auto" : undefined,
      }}
    >
      <div
        style={{
          padding: profileControlsLandscape ? "6px 12px 0" : mobileMode ? "10px 14px 0" : 0,
          minWidth: 0,
          overflow: "visible",
        }}
      >
        {controls}
      </div>

      {statsView === "logs" && showPerformanceChart ? (
        <div
          style={{
            borderRadius: mobileMode ? 0 : 16,
            border: mobileMode ? "none" : "1px solid rgba(255,255,255,0.08)",
            background: "rgba(18,18,22,0.98)",
            padding: mobileMode ? "14px 10px 12px" : "18px 18px 14px",
            display: "grid",
            gap: mobileMode ? 10 : 14,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "grid", gap: 7, minWidth: 0 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: mobileMode ? 18 : 22,
                    fontWeight: 900,
                    color: "#fff",
                    lineHeight: 1.05,
                  }}
                >
                  Player Performance
                </div>
                <button
                  type="button"
                  aria-label={performanceChartHidden ? "Show performance chart" : "Hide performance chart"}
                  onClick={() => setPerformanceChartHidden((current) => !current)}
                  style={{
                    appearance: "none",
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.84)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flex: "0 0 auto",
                  }}
                >
                  {performanceChartHidden ? (
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                      <path
                        d="M2.4 12s3.4-6 9.6-6 9.6 6 9.6 6-3.4 6-9.6 6-9.6-6-9.6-6Zm9.6 3.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                      <path
                        d="M3 4.4 19.6 21l-1.4 1.4-3.2-3.2A13.6 13.6 0 0 1 12 18c-6.2 0-9.6-6-9.6-6a20.3 20.3 0 0 1 4.2-4.9L1.6 3 3 1.6l18 18-1.4 1.4-3.2-3.2L3 4.4Zm6 6 4.6 4.6a3.1 3.1 0 0 1-4.6-4.6Zm3-4.4c6.2 0 9.6 6 9.6 6a20 20 0 0 1-3.6 4.4l-2.3-2.3a6 6 0 0 0-8.1-8.1l-1.9-1.9A12.5 12.5 0 0 1 12 6Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {performanceChartLegendItems.length ? (
                <div
                  aria-label="Performance chart color key"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: mobileMode ? 8 : 10,
                    flexWrap: "wrap",
                    color: "rgba(255,255,255,0.78)",
                    fontSize: mobileMode ? 10 : 12,
                    fontWeight: 850,
                    lineHeight: 1,
                  }}
                >
                  {performanceChartLegendItems.map((item) => (
                    <span key={`${item.color}-${item.label}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <span
                        aria-hidden
                        style={{
                          width: mobileMode ? 9 : 10,
                          height: mobileMode ? 9 : 10,
                          borderRadius: 3,
                          background: item.color,
                          boxShadow: "0 0 0 1px rgba(255,255,255,0.12)",
                        }}
                      />
                      <span>{item.label}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                color: "rgba(255,255,255,0.92)",
                fontWeight: 900,
              }}
            >
              <button
                type="button"
                onClick={() => newerSeason && setSelectedSeason(newerSeason.season)}
                disabled={!newerSeason}
                style={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  color: newerSeason ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.28)",
                  cursor: newerSeason ? "pointer" : "default",
                  fontSize: mobileMode ? 22 : 28,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ‹
              </button>
              <div style={{ fontSize: mobileMode ? 16 : 18, whiteSpace: "nowrap" }}>
                Season {activeSeason.season}
              </div>
              <button
                type="button"
                onClick={() => olderSeason && setSelectedSeason(olderSeason.season)}
                disabled={!olderSeason}
                style={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  color: olderSeason ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.28)",
                  cursor: olderSeason ? "pointer" : "default",
                  fontSize: mobileMode ? 22 : 28,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ›
              </button>
            </div>
          </div>

          {!performanceChartHidden ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobileMode ? "30px minmax(0, 1fr)" : "36px minmax(0, 1fr)",
                gap: mobileMode ? 8 : 12,
                alignItems: "stretch",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateRows: `repeat(${chartTicks.length}, 1fr)`,
                  alignItems: "end",
                  color: "rgba(255,255,255,0.78)",
                  fontSize: mobileMode ? 10 : 12,
                  fontWeight: 800,
                  textAlign: "right",
                  paddingBottom: 26,
                }}
              >
                {[...chartTicks].reverse().map((tick) => (
                  <div key={tick}>{tick}</div>
                ))}
              </div>

              <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                <div
                  style={{
                    position: "relative",
                    height: mobileMode ? 220 : 280,
                    borderLeft: "1px solid rgba(255,255,255,0.16)",
                    borderBottom: "1px solid rgba(255,255,255,0.16)",
                    display: "grid",
                    gridTemplateColumns: `repeat(${Math.max(1, performanceChartRows.length)}, minmax(0, 1fr))`,
                    gap: mobileMode ? 6 : 10,
                    alignItems: "end",
                    padding: mobileMode ? "10px 6px 0" : "10px 10px 0",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  {chartTicks.slice(1).map((tick) => (
                    <div
                      key={tick}
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: `${(tick / chartMax) * 100}%`,
                        borderTop: "1px dashed rgba(255,255,255,0.08)",
                      }}
                    />
                  ))}

                  {performanceChartRows.map((row) => {
                    const barHeight = `${Math.max(0, Math.min(100, (row.points / chartMax) * 100))}%`;
                    return (
                      <div
                        key={`chart-${row.week}`}
                        style={{
                          position: "relative",
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            bottom: `calc(${barHeight} + 10px)`,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: mobileMode ? 10 : 12,
                            fontWeight: 800,
                            color: "rgba(255,255,255,0.82)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.label}
                        </div>
                        <div
                          style={{
                            width: "100%",
                            maxWidth: mobileMode ? 16 : 18,
                            height: barHeight,
                            minHeight: row.points > 0 ? 8 : 2,
                            borderRadius: "4px 4px 0 0",
                            background: row.color,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${Math.max(1, performanceChartRows.length)}, minmax(0, 1fr))`,
                    gap: mobileMode ? 6 : 10,
                    padding: mobileMode ? "0 6px" : "0 10px",
                    color: "rgba(255,255,255,0.88)",
                    fontSize: mobileMode ? 11 : 13,
                    fontWeight: 800,
                    textAlign: "center",
                    minWidth: 0,
                  }}
                >
                  {performanceChartRows.map((row) => (
                    <div key={`chart-week-${row.week}`}>{row.week}</div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        ref={scrollRef}
        key={`${activeSeason.season}-${selectedScoring}`}
        style={{
          overflowX: "auto",
          overflowY: "auto",
          borderRadius: mobileMode ? 0 : 16,
          border: mobileMode ? "none" : "1px solid rgba(255,255,255,0.08)",
          background: "rgba(18,18,22,0.98)",
          maxHeight: mobileMode ? undefined : "min(58vh, 760px)",
          flex: mobileMode ? "1 1 auto" : undefined,
          minHeight: 0,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
        }}
        className="playerGameLogScroll"
      >
        <table
          style={{
            width: "max-content",
            minWidth: "100%",
            tableLayout: "auto",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <colgroup>
            {flatColumns.map((column, index) => {
              const width = getColumnWidth(column, index);

              return <col key={`col-${column.label}-${index}`} style={width ? { width } : undefined} />;
            })}
          </colgroup>
          <thead>
            <tr>
              {flatColumns.map((column, index) => (
                <th
                  key={`${column.label}-${index}`}
                  style={{
                    padding: mobileMode && index === 0 ? "10px 4px" : cellPadding,
                    textAlign: column.align ?? "center",
                    fontSize: headerFontSize,
                    fontWeight: 900,
                    letterSpacing: compactTableMode ? 0.2 : 0.55,
                    color: "rgba(255,255,255,0.72)",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    background: "#101014",
                    position: "sticky",
                    top: 0,
                    zIndex: index === 0 ? 5 : index === 1 ? 4 : 3,
                    left: index === 0 ? 0 : index === 1 ? logFirstColumnWidth : undefined,
                    boxShadow:
                      index === 1
                        ? "1px 0 0 rgba(255,255,255,0.08), 8px 0 14px rgba(0,0,0,0.22)"
                        : index === 0
                          ? "1px 0 0 rgba(255,255,255,0.06)"
                          : undefined,
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, index) => (
              <tr
                key={`${activeSeason.season}-${row.week}-${row.opponent}-${index}`}
                style={{
                  background: index % 2 === 0
                    ? "rgba(0,0,0,0.34)"
                    : "rgba(35,35,35,0.9)",
                }}
              >
                {flatColumns.map((column, columnIndex) => {
                  const value = column.getValue(row);
                  const rowBackground = index % 2 === 0 ? "#09090d" : "#2a2a2a";
                  const style: React.CSSProperties = column.highlight
                    ? gridHighlightCellStyle
                    : column.align === "left"
                      ? { ...gridCellStyle, textAlign: "left", fontWeight: 800 }
                      : gridNumericCellStyle;
                  const displayValue =
                    column.label === "WK"
                      ? (value == null ? "-" : String(value))
                      : formatMetric(value, column.digits ?? 0, column.fixedDigits ?? false);
                  const numericValue = typeof value === "number" ? value : undefined;
                  const heatmapStyle = getHeatmapCellStyle(
                    !PLAYER_PROFILE_STAT_COLOR_CODING_ENABLED || columnIndex <= 1
                      ? null
                      : getHeatmapTier(heatmapScales[String(columnIndex)], numericValue)
                  );

                  return (
                    <td
                      key={`${activeSeason.season}-${row.week}-${column.label}-${columnIndex}`}
                      style={{
                        ...style,
                        position: columnIndex === 0 || columnIndex === 1 ? "sticky" : undefined,
                        left: columnIndex === 0 ? 0 : columnIndex === 1 ? logFirstColumnWidth : undefined,
                        zIndex: columnIndex === 0 ? 2 : columnIndex === 1 ? 1 : undefined,
                        background:
                          columnIndex === 0 || columnIndex === 1
                            ? rowBackground
                            : style.background,
                        boxShadow:
                          columnIndex === 1
                            ? "1px 0 0 rgba(255,255,255,0.06), 8px 0 14px rgba(0,0,0,0.18)"
                            : columnIndex === 0
                              ? "1px 0 0 rgba(255,255,255,0.04)"
                              : undefined,
                      }}
                    >
                      {columnIndex === 0 || columnIndex === 1 ? (
                        displayValue
                      ) : (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "max-content",
                            borderRadius: statPillRadius,
                            padding: statPillPadding,
                            background: heatmapStyle.background,
                            color: heatmapStyle.color,
                            fontSize: cellFontSize,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {displayValue}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
